import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import RichTextDisplay from '../../components/common/RichTextDisplay';
import BackButton from '../../components/common/BackButton';
import './CoinDetail.css';

function CoinDetail() {
  const auth = useAuth();
  const { txId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    const fetchTransactionDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        // Use public endpoint if not authenticated, otherwise use authenticated endpoint
        const data = auth.isAuthenticated && auth.user?.id_token
          ? await apiService.getTransactionById(auth.user.id_token, txId)
          : await apiService.getPublicTransactionById(txId);
        setTransaction(data);
      } catch (err) {
        console.error('Error fetching transaction details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (txId) {
      fetchTransactionDetail();
    }
  }, [txId, auth.isAuthenticated, auth.user]);

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleCopyLink = async () => {
    const transactionUrl = `${window.location.origin}/transaction/${txId}`;
    try {
      await navigator.clipboard.writeText(transactionUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleShareToLinkedIn = () => {
    // Use the share endpoint URL which has OG tags for LinkedIn preview
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';
    const shareUrl = `${apiBaseUrl}/api/transactions/share/${txId}`;
    // Use LinkedIn's share URL format - this will show the OG preview from the share endpoint
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="coin-detail-container">
        <div className="coin-detail-content">
          <div className="loading-container">
            <p>Loading transaction details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="coin-detail-container">
        <div className="coin-detail-content">
          <div className="error-container">
            <h2>Error Loading Transaction</h2>
            <p>{error}</p>
            <BackButton label="Go Back" variant="light" />
          </div>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="coin-detail-container">
        <div className="coin-detail-content">
          <div className="error-container">
            <h2>Transaction Not Found</h2>
            <p>The transaction you're looking for doesn't exist.</p>
            <BackButton label="Go Back" variant="light" />
          </div>
        </div>
      </div>
    );
  }

  const currentUserId = auth.user?.profile?.sub;
  const isSender = currentUserId === transaction.sender_id;
  const isReceiver = currentUserId === transaction.receiver_id;

  return (
    <div className="coin-detail-container">
      <div className="coin-detail-content">
        <div className="detail-actions-bar">
          <BackButton variant="light" />
          <button onClick={() => setShowShareModal(true)} className="share-button">
            Share
          </button>
        </div>

        <div className="coin-detail-card">
        <div className="card-meta">
          {/* Note that Coin ID here is actually a misnomer, as this is the transaction id.
          It is more intuitive to call it Coin ID for the user. Eventually we could probably merge
          the coin id and transaction id into one thing and rid ourselves of the confusion,
          but that would require some db changes so better to stick with what we have for now. */}
          <span className="card-tx-id">Coin ID: {txId}</span>
          <span className="card-timestamp">{formatDateTime(transaction.created_at)}</span>
        </div>
        {/* Parties Involved - Compact Design */}
        <div className="transfer-row">
          <div
            className={`transfer-party ${isSender ? 'is-you' : ''}`}
            onClick={() => navigate(`/profile/${transaction.sender_id}`)}
          >
            <div className="transfer-avatar">
              {transaction.sender_name?.charAt(0)?.toUpperCase() || 'S'}
            </div>
            <div className="transfer-info">
              <span className="transfer-name">
                {transaction.sender_name}
                {isSender && <span className="you-tag">You</span>}
              </span>
              {transaction.sender_job_title && (
                <span className="transfer-title">{transaction.sender_job_title}</span>
              )}
            </div>
          </div>

          <div className="transfer-arrow">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>

          <div
            className={`transfer-party ${isReceiver ? 'is-you' : ''}`}
            onClick={() => navigate(`/profile/${transaction.receiver_id}`)}
          >
            <div className="transfer-avatar">
              {transaction.receiver_name?.charAt(0)?.toUpperCase() || 'R'}
            </div>
            <div className="transfer-info">
              <span className="transfer-name">
                {transaction.receiver_name}
                {isReceiver && <span className="you-tag">You</span>}
              </span>
              {transaction.receiver_job_title && (
                <span className="transfer-title">{transaction.receiver_job_title}</span>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <section className="detail-section">
          <h2>Description</h2>
          <div className="detail-value description">
            <RichTextDisplay content={transaction.description} />
          </div>
        </section>

        </div>

        {/* Share Modal */}
        {showShareModal && (
          <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
            <div className="share-modal" onClick={(e) => e.stopPropagation()}>
              <div className="share-modal-header">
                <h2>Share Transaction</h2>
                <button className="close-modal-button" onClick={() => setShowShareModal(false)}>
                  ×
                </button>
              </div>

              <div className="share-modal-content">
                <div className="share-link-container">
                  <input
                    type="text"
                    value={`${window.location.origin}/transaction/${txId}`}
                    readOnly
                    className="share-link-input"
                  />
                  <button
                    onClick={handleCopyLink}
                    className={`copy-link-button ${copySuccess ? 'success' : ''}`}
                  >
                    {copySuccess ? '✓ Copied!' : 'Copy Link'}
                  </button>
                </div>

                <div className="linkedin-share-section">
                  <button onClick={handleShareToLinkedIn} className="linkedin-share-button">
                    <svg className="linkedin-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
                    </svg>
                    Share on LinkedIn
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoinDetail;
